import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Skeleton,
  Alert,
  Card,
  Divider,
  useTheme,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import { useSelector } from "react-redux";
import { investorApi } from "../../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecondaryButton from "../../common/SecondaryButton";
import GradientButton from "../../common/GradiantButton";

const AddWallet = () => {

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const userId = useSelector((state) => state.user.userId);
  const { state } = useLocation();
  const navigate = useNavigate();
  const walletBalance = state?.walletBalance || 0;
  const theme = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((data) => data.themeValue.theme);

  const validateAmount = () => {
    if (!amount) {
      setError("Please enter an amount");
      return false;
    }
    if (isNaN(amount) || parseFloat(amount) <= 0) {
      setError("Please enter a valid positive number");
      return false;
    }
    if (parseFloat(amount) > 1000000) {
      setError("Maximum amount is $1,000,000");
      return false;
    }
    return true;
  };

  const handleAddBalance = async () => {
    if (!validateAmount()) return;

    setSubmitting(true);
    setError("");

    try {
      const response = await investorApi.addWalletBalance({
        UserId: userId,
        Amount: parseFloat(amount),
      });

      if (response.status === 200) {
        navigate("/wallet", { state: { success: true } });
      } else {
        setError("Failed to add amount. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
      {/* Balance Card */}
      {loading ? (
        <Skeleton
          variant="rounded"
          width="100%"
          height={120}
          sx={{ mb: 3, borderRadius: 3 }}
        />
      ) : (
        <Card
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: theme.background,
            // boxShadow: theme.shadows[2],
            border: `1px solid ${theme.borderColor}`
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <AccountBalanceWalletIcon
              color="primary"
              sx={{ fontSize: 40 }}
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ color: theme.text }}>
                Current Balance
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: theme.text }}>
                ₹ {walletBalance.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Card>
      )}

      {/* Add Balance Form */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${theme.borderColor}`,
          // boxShadow: theme.shadows[1]
          backgroundColor: theme.background
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={2} sx={{ color: theme.text }}>
          Add Funds to Wallet
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Amount"
          variant="outlined"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error}
          type="number"
          InputProps={{
            
            inputProps: { min: 0, step: "0.01" }
          }}
          sx={{
            mb: 3, input: {
              color: theme.text,
            },
            label: {
              color: theme.text,
            },
            '& label.Mui-focused': {
              color: theme.primary_color,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.text,
              },
              '&:hover fieldset': {
                borderColor: theme.primary_color,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.primary_color,
              },
            },
          }}
          placeholder="0.00"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ textAlign: "center", alignItems: "center", gap: "10px" }}>
          <GradientButton
            onClick={handleAddBalance}
            disabled={submitting || !amount}
            fullWidth
            height={56}         // close to your original 1.5 padding + default height
            width="90%"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 16,
            }}
            text={
              submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Add Funds"
              )
            }
          />
          <SecondaryButton
            text="Cancel"
            onClick={() => navigate(-1)}
            sx={{
              width: "90%",
              py: 1.5,
              borderRadius: 2,
              marginTop: 2
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default AddWallet;